/**
 * CÓMO FUNCIONAN LOS PUNTOS, explicado.
 *
 * QUÉ PROBLEMA RESUELVE. El ranking daba números y nadie sabía de dónde
 * salían: por qué un torneo ganado vale lo que vale, por qué perder suma,
 * por qué el quinto partido contra el mismo amigo casi no cuenta. Esa
 * explicación existía en un PDF que hay que mandar por WhatsApp y que queda
 * viejo el día que se toca una constante. Javier la pidió adentro de la app.
 *
 * TODOS LOS NÚMEROS SALEN DE LAS CONSTANTES, ni uno está escrito a mano en el
 * texto. Es la única forma de que esta pantalla no mienta: el día que un
 * torneo deje de valer 250, acá va a decir el número nuevo sin que nadie se
 * acuerde de venir. Por eso las tres constantes de los retos se mudaron al
 * contrato — antes vivían solo en el servidor y esta pantalla no las veía.
 *
 * NO ES LA FÓRMULA, ES LO QUE SIGNIFICA. Quien entra acá no quiere leer
 * `rawWinPoints`: quiere saber si le conviene jugar el torneo del sábado o
 * retar a tres personas. Los ejemplos están en esas unidades.
 *
 * ABIERTA A CUALQUIERA CON CUENTA. Son las reglas del juego: esconderlas
 * detrás de un rol sería pedirle a la gente que confíe en una cuenta que no
 * puede ver.
 */
import { useTranslation } from 'react-i18next';
import {
  CHALLENGE_LOSS_POINTS,
  MÁXIMO_DE_TABLAS,
  CHALLENGE_WIN_POINTS,
  rawWinPoints,
  FRIENDLY_FACTOR,
  RANKING_BASE_BY_SIZE,
  RANKING_SHARE_BY_ROUND,
  RANKING_WINDOW_DAYS,
} from '@tennisup/contracts';

import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';

/**
 * LO QUE VALE GANAR UN TORNEO.
 *
 * Se lee de la tabla y no se escribe 250: la tabla es por tamaño de cuadro y
 * hoy todos valen lo mismo, pero eso puede volver a cambiar. Si algún día
 * dejan de ser iguales, esta pantalla lo va a decir en vez de mentir.
 */
const BASE = RANKING_BASE_BY_SIZE[128];

/** El cuadro más chico y el más grande que la aplicación arma. */
const TAMAÑOS = Object.keys(RANKING_BASE_BY_SIZE)
  .map(Number)
  .sort((a, b) => a - b);
const CHICO = TAMAÑOS[0] ?? 2;
const GRANDE = TAMAÑOS[TAMAÑOS.length - 1] ?? 128;

/** Las semanas que valen los puntos, redondeadas: 364 días son 52 semanas. */
const SEMANAS = Math.round(RANKING_WINDOW_DAYS / 7);

/**
 * Un reto ganado PAREJO, que es el caso normal y la REFERENCIA de la escala.
 *
 * ERA `CHALLENGE_WIN_POINTS / 2` y estaba bien mientras la constante fuera el
 * techo —el máximo se alcanzaba contra alguien muchísimo mejor y lo parejo
 * pagaba la mitad—. Ahora la constante ES lo que paga un parejo, así que
 * dividir daba la mitad de lo correcto.
 */
const RETO_PAREJO = CHALLENGE_WIN_POINTS;

/**
 * LOS DOS EXTREMOS DE LA ESCALA, para la tabla.
 *
 * SE CALCULAN CON LA MISMA FÓRMULA DEL SERVIDOR (`rawWinPoints`) y no a mano:
 * si se escribieran, el día que cambie el piso o el techo esta pantalla
 * seguiría diciendo los viejos. Es la misma razón por la que ningún número de
 * acá está escrito en el texto.
 *
 * `4000` y `0` son «muchísimo mejor» y «muchísimo peor»: la curva ya está
 * plana mucho antes, así que cualquier diferencia grande da lo mismo.
 */
const RETO_CONTRA_MEJOR = rawWinPoints(0, 4000);
const RETO_CONTRA_PEOR = rawWinPoints(4000, 0);
const AMISTOSO_GANADO = Math.round(RETO_PAREJO * FRIENDLY_FACTOR);
const AMISTOSO_PERDIDO = Math.round(CHALLENGE_LOSS_POINTS * FRIENDLY_FACTOR);

/** Cuántos retos parejos ganados igualan un torneo. La cuenta de Javier. */
const RETOS_POR_TORNEO = Math.round(BASE / RETO_PAREJO);
const DERROTAS_POR_TORNEO = Math.round(BASE / CHALLENGE_LOSS_POINTS);

export function PointsPage(): JSX.Element {
  const { t } = useTranslation();

  /** La escalera del cuadro, de campeón para abajo. */
  const rondas = [1, 2, 4, 8, 16, 32, 64, 128] as const;

  return (
    <AppLayout>
      <BackButton to="/mas" />
      {/* `points-page` existe por una sola cosa: alinear el texto a la
          izquierda. Las pantallas de esta aplicación centran su contenido, y
          seis párrafos de explicación centrados se leen como un poema — el
          ojo pierde dónde empieza cada renglón. */}
      <div className="points-page">
      <h1>{t('points.title')}</h1>
      <p className="lead">{t('points.lead')}</p>

      <section>
        <h2>{t('points.tournamentsTitle')}</h2>
        {/* LOS TAMAÑOS TAMBIÉN SALEN DE LA TABLA: son las llaves de
            `RANKING_BASE_BY_SIZE`, así que el texto no puede quedar hablando
            de cuadros que la aplicación ya no arma. */}
        <p>{t('points.tournamentsBody', { base: BASE, small: CHICO, big: GRANDE })}</p>

        {/*
          LA ESCALERA COMO TABLA: son ocho números y una frase por cada uno
          sería un párrafo que nadie termina. Acá se busca el propio.

          DOS COLUMNAS Y NO TRES. Con «Parte» y «Puntos» separadas, en un
          teléfono de 390 px la tercera quedaba cortada por el borde — y la
          cortada era justo la que la gente viene a ver, porque «40 %» obliga
          a hacer una multiplicación mental y «100 puntos» se lee. Van juntas:
          el número grande y el porcentaje debajo, que es contexto.
        */}
        <div className="table-scroll">
        <table className="data-table points-table">
          <thead>
            <tr>
              <th scope="col">{t('points.round')}</th>
              <th scope="col" className="num">
                {t('points.gets')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rondas.map((quedaban) => {
              const parte = RANKING_SHARE_BY_ROUND[quedaban] ?? 0;
              return (
                <tr key={quedaban}>
                  <td>{t(`points.round${quedaban}`)}</td>
                  <td className="num">
                    <strong>{Math.round(parte * BASE)}</strong>
                    <span className="help">{Math.round(parte * 100)}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <p className="help">{t('points.roundHelp')}</p>
      </section>

      <section>
        <h2>{t('points.challengesTitle')}</h2>
        <p>{t('points.challengesBody', { even: RETO_PAREJO })}</p>

        {/*
          LA TABLA, que es lo que Javier pidió al no entender por qué Charly
          sacó más perdiendo que Pekas ganando: «actualiza como se asignan los
          puntos si puedes agregar esa tabla seria ideal».

          LOS NÚMEROS SALEN DE LAS CONSTANTES, nunca escritos a mano — hay una
          prueba que lo hace cumplir. Son los EXTREMOS de la escala y lo
          parejo; los valores de en medio se interpolan y no hace falta
          listarlos para entender la idea.
        */}
        <div className="table-scroll">
          <table className="people-table table-compact">
            <thead>
              <tr>
                <th scope="col">{t('points.tableRival')}</th>
                <th scope="col" className="num">{t('points.tableWin')}</th>
                <th scope="col" className="num">{t('points.tableLoss')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('points.rivalBetter')}</td>
                <td className="num">{RETO_CONTRA_MEJOR}</td>
                <td className="num">{CHALLENGE_LOSS_POINTS}</td>
              </tr>
              <tr>
                <td><strong>{t('points.rivalEven')}</strong></td>
                <td className="num"><strong>{RETO_PAREJO}</strong></td>
                <td className="num">{CHALLENGE_LOSS_POINTS}</td>
              </tr>
              <tr>
                <td>{t('points.rivalWorse')}</td>
                <td className="num">{RETO_CONTRA_PEOR}</td>
                <td className="num">{CHALLENGE_LOSS_POINTS}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>{t('points.lossBody', { loss: CHALLENGE_LOSS_POINTS })}</p>
        <p className="help">{t('points.alwaysWorthWinning')}</p>

        {/* LA EQUIVALENCIA ES LO QUE LA GENTE QUIERE SABER de verdad: si le
            conviene el torneo del sábado o retar a tres personas. */}
        <p>
          <strong>
            {t('points.equivalence', {
              wins: RETOS_POR_TORNEO,
              losses: DERROTAS_POR_TORNEO,
              base: BASE,
            })}
          </strong>
        </p>
      </section>

      {/*
        CRUZAR DE CATEGORÍA VA EN SU PROPIA SECCIÓN, justo después de los
        retos y antes de los amistosos. Es la pregunta que se hace apenas se
        entiende que ganar vale distinto según el rival —«¿entonces puedo
        retar al mejor del club?»— y contestarla en el mismo párrafo de
        arriba la escondería en la mitad de un texto.
      */}
      <section>
        <h2>{t('points.crossTitle')}</h2>
        <p>{t('points.crossBody')}</p>
      </section>

      {/*
        LAS TRES DE «ESTAR EN DOS CATEGORÍAS» VAN JUNTAS Y EN ESTE ORDEN.

        Primero que se puede, después qué pasa con los puntos, y al final en
        cuál cae cada reto. Es el orden en que aparecen las dudas: nadie se
        pregunta «¿en qué tabla cuenta mi reto?» antes de saber que puede
        estar en dos.

        Y van DESPUÉS de «cruzar de categoría» porque esa explica que se
        puede retar a otra categoría sin estar en ella; sin leerla antes, las
        dos cosas se confunden en una sola.
      */}
      <section>
        <h2>{t('points.twoTablesTitle')}</h2>
        <p>{t('points.twoTablesBody', { max: MÁXIMO_DE_TABLAS })}</p>
      </section>

      <section>
        <h2>{t('points.separateTitle')}</h2>
        <p>{t('points.separateBody')}</p>
        <p className="help">{t('points.separateHelp')}</p>
      </section>

      <section>
        <h2>{t('points.whichTableTitle')}</h2>
        <p>{t('points.whichTableBody')}</p>
      </section>

      <section>
        <h2>{t('points.friendlyTitle')}</h2>
        <p>
          {t('points.friendlyBody', { won: AMISTOSO_GANADO, lost: AMISTOSO_PERDIDO })}
        </p>
        <p className="help">{t('points.drawHelp')}</p>
      </section>

      <section>
        <h2>{t('points.repeatTitle')}</h2>
        <p>{t('points.repeatBody')}</p>
      </section>

      <section>
        <h2>{t('points.windowTitle')}</h2>
        <p>{t('points.windowBody', { weeks: SEMANAS })}</p>
      </section>

      {/*
        DEBAJO DE «LOS PUNTOS VENCEN» porque las dos hablan de perder puntos
        sin jugar, y son distintas: aquella los deja caer solos por viejos,
        esta los descuenta a propósito. Separadas, cualquiera creería que es
        la misma contada dos veces.

        SIN NÚMERO: cuánto descuenta lo elige cada club, así que decir uno acá
        sería inventarlo. La pantalla no sabe en qué club está mirando.
      */}
      <section>
        <h2>{t('points.inactivityTitle')}</h2>
        <p>{t('points.inactivityBody')}</p>
        <p className="help">{t('points.inactivityHelp')}</p>
      </section>

      <section>
        <h2>{t('points.roundingTitle')}</h2>
        <p>{t('points.roundingBody')}</p>
      </section>
      </div>
    </AppLayout>
  );
}
